import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import swimService from '../api/services/swimService';
import { SwimEnrollment } from '../types/swim';

function enrolleeName(e: SwimEnrollment): string {
  const en = e.enrollee;
  if (!en) return '—';
  return `${en.first_name ?? ''} ${en.last_name ?? ''}`.trim() || '—';
}

function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

/**
 * Staff view: recently cancelled swim enrollments + the reason the swimmer/parent gave,
 * so coaches know who dropped and why. Read-only.
 */
const ProgramCancellationsPage: React.FC = () => {
  const [items, setItems] = useState<SwimEnrollment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { void load(); }, []);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setItems(await swimService.listCancellations());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load cancellations');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h4" component="h1">Program Cancellations</Typography>
          <Typography variant="body2" color="text.secondary">
            Recently cancelled swim enrollments and the reason given — so coaches know who
            dropped and why.
          </Typography>
        </Box>
        <IconButton onClick={() => void load()} disabled={loading} aria-label="Refresh">
          <RefreshIcon />
        </IconButton>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper variant="outlined">
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : items.length === 0 ? (
          <Box sx={{ p: 3 }}>
            <Typography color="text.secondary">No cancellations.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Swimmer</TableCell>
                  <TableCell>Program</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Cancelled</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell>{enrolleeName(e)}</TableCell>
                    <TableCell>{e.program?.name ?? '—'}</TableCell>
                    <TableCell sx={{ maxWidth: 360, overflowWrap: 'anywhere' }}>
                      {e.cancellation_reason ? (
                        e.cancellation_reason
                      ) : (
                        <Typography component="span" variant="body2" color="text.disabled">
                          — (no reason given)
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{formatWhen(e.cancelled_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Container>
  );
};

export default ProgramCancellationsPage;
